<template>
  <div
    class="scheduler-card"
    :style="{ '--agenda-sticky-offset': computedStickyTop + 'px' }"
  >
    <div class="agenda-notify" v-if="notification?.enabled" :class="notification?.enabled ? 'mtb-desktop-15' : ''">
      <Notify
        :theme="notification?.type"
        v-html="notification?.text"
      ></Notify>
    </div>

    <welcome-message />

    <div class="agenda-notify" v-if="!isSingleSession && conferenceHasEnded" :class="notification?.enabled ? 'mtb-desktop-15' : ''">
      <Notify theme="primary">
        Now that your event has concluded make sure to visit the
        <a href="#" @click.prevent="goToCertificatePage()" style="text-decoration: underline;">certificate page</a>
        to complete your evaluations and access your certificate.
      </Notify>
    </div>

    <div v-if="isFetching" class="page-loader">
      <c-loader/>
    </div>

    <additional-fixed-tabs
      :tabs="isMobile ? tabs : []"
      additional-fixed-tabs-class="scheduler-mobile-tabs"
      :show-filter-button="false"
    />

    <!-- Порожній розклад - показуємо календар за замовчуванням -->
    <calendar-view
      v-if="!isFetching && !filteredSessions.length && !errorStatus"
      :agenda="agendaForCalendar"
      initial-view="calendar"
      :attendee-id="attendeeId"
      :view-tabs="[]"
      @add-to-schedule="handleAddToSchedule"
      @open-session="handleOpenSession"
    />

    <!-- Є сесії - звичайна логіка з перемиканням видів -->
    <calendar-view
      v-if="!isFetching && visibleSessions?.length && !errorStatus"
      :agenda="agendaForCalendar"
      :initial-view="currentView"
      :attendee-id="attendeeId"
      :view-tabs="viewTabs"
      @add-to-schedule="handleAddToSchedule"
      @open-session="handleOpenSession"
      @view-changed="currentView = $event"
    />

    <!-- List View -->
    <div
      v-if="currentView === 'list' && !isFetching && visibleSessions?.length && !errorStatus"
      class="agenda-page"
    >
      <div v-if="!isFetching && !visibleSessions?.length && !errorStatus">
        <c-list-empty
          style="margin-top: 50px;"
          title="The list is empty"
          description="You haven't added any sessions to your schedule yet"
        ></c-list-empty>
      </div>

      <div v-else-if="!isFetching && !visibleSessions?.length && errorStatus">
        <ErrorPage :status="errorStatus"/>
      </div>

      <div v-else class="agenda-group" ref="agendaGroup">
        <!-- Global sticky day header -->
        <div
          v-show="currentStickyDay"
          ref="stickyDayBar"
          class="sticky-day-header"
          :style="{ transform: `translateY(-${stickyPushOffset}px)` }"
        >
          <div class="sticky-day-header__left">
            <h3>{{ currentStickyDay }}</h3>
          </div>

          <view-toggle
            v-if="!isFetching && visibleSessions.length > 0"
            :tabs="viewTabs"
            :current-view="currentView"
            :hide-label-on-mobile="true"
            @view-changed="switchView"
          />
        </div>

        <div v-if="visibleSessions.length" class="agenda-day">
          <div>
            <div
              v-for="(session, index) in visibleSessions"
              :key="session.id"
              class="agenda-list"
              :class="{ 'first-item': index === 0 }"
            >
              <!-- Item day header -->
              <div class="agenda-list-day" v-if="shouldShowDay(index)">
                <h3>{{ formatDate(session.start_at) }}</h3>
              </div>

              <agenda-item
                current-page="My Schedule"
                :session="session"
                :index="index"
                :allow-add-to-scheduler="attendee?.tabs?.my_agenda"
                :group-time-frame="
                  visibleSessions[index - 1] &&
                  visibleSessions[index - 1].start_at_formatted ===
                  visibleSessions[index].start_at_formatted &&
                  visibleSessions[index - 1].end_at_formatted ===
                  visibleSessions[index].end_at_formatted
                "
              />
            </div>

            <div
              v-if="visibleSessionsCount < filteredSessions.length"
              class="load-more-trigger"
              v-observe-visibility="handleVisibilityChange"
            >
              <c-button
                fill
                uppercase
                theme="default"
                icon="fa-solid fa-arrow-down"
                size="sm"
                :show-indicator="true"
                @click="loadMore"
              >
                Load More ...
              </c-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import common from "../../../helpers/common";
import Notify from "../../../components/ui/Notify";
import WelcomeMessage from "../common/welcome-message";
import AgendaItem from "../common/agenda-item";
import AdditionalFixedTabs from "../../../components/ui/AdditionalFixedTabs";
import ErrorPage from "../../error-page";
import CalendarView from "./scheduler";
import ViewToggle from "../../../components/ui/ViewToggle/index.vue";

export default {
  components: {
    Notify,
    WelcomeMessage,
    AgendaItem,
    AdditionalFixedTabs,
    ErrorPage,
    CalendarView,
    ViewToggle,
  },
  beforeMount() {
    this.$store.commit("agendaStore/updateIsLoadedSessionDetails", false);
  },
  async mounted() {
    common.setHeaderText('Schedule');

    await this.$store.dispatch("agendaStore/fetchAgenda", {
      attendeeId: this.attendeeId,
      conferenceId: this.attendee?.conference_id
    });

    // Reactive width + rebuild observer on resize
    this._onResize = () => {
      this.windowWidth = window.innerWidth;
      this.computeStickyTop();
      this.rebuildDayObserver();
      this._requestScrollMeasure();
    };
    window.addEventListener('resize', this._onResize, { passive: true });

    // rAF-throttled scroll fallback
    this._onScroll = () => {
      this._requestScrollMeasure();
    };
    window.addEventListener('scroll', this._onScroll, { passive: true });

    // Додаємо подвійний nextTick для коректного розрахунку висоти табів
    this.$nextTick(() => {
      this.$nextTick(() => {
        this.computeStickyTop();
        this.rebuildDayObserver();
        this._requestScrollMeasure();
      });
    });
  },
  beforeDestroy() {
    this.disconnectDayObserver();
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('scroll', this._onScroll);
    if (this._scrollRaf) {
      cancelAnimationFrame(this._scrollRaf);
      this._scrollRaf = 0;
    }
  },
  data() {
    return {
      attendeeId: this.$route.params.attendeeId,
      windowWidth: window.innerWidth,

      // Sticky header state
      currentStickyDay: '',
      stickyPushOffset: 0,
      computedStickyTop: 80,

      // IntersectionObserver handles
      _dayIO: null,
      _observedDayHeaders: [],

      // rAF throttle id
      _scrollRaf: 0,

      viewTabs: [
        { value: 'list', label: 'List', icon: 'fa fa-list' },
        { value: 'calendar', label: 'Calendar', icon: 'fa fa-calendar' }
      ]
    }
  },
  computed: {
    ...mapGetters('attendeeStore', [
      'attendee',
      'tabs',
      'notification',
      'isSingleSession',
      'conferenceHasEnded',
    ]),

    ...mapGetters('agendaStore', [
      'isFetching',
      'flatSessions',
      'visibleSessionsCount',
      'defaultSessionsCount',
      'errorStatus',
      'myScheduleView'
    ]),

    filteredSessions() {
      return this.flatSessions.filter(session => session.my_agenda === true);
    },

    visibleSessions() {
      return this.filteredSessions.slice(0, this.visibleSessionsCount);
    },

    isMobile() {
      return this.windowWidth < 900;
    },

    agendaForCalendar() {
      const dayMap = new Map();

      this.flatSessions.forEach(session => {
        const dayKey = session.happens_at;
        if (!dayMap.has(dayKey)) {
          dayMap.set(dayKey, {
            id: session.day_id,
            date_long: this.formatDate(session.start_at),
            sessions: []
          });
        }
        dayMap.get(dayKey).sessions.push(session);
      });

      return Array.from(dayMap.values());
    },

    currentView: {
      get() {
        return this.myScheduleView;
      },
      set(value) {
        this.$store.commit('agendaStore/setMyScheduleView', value);
      }
    },
  },
  watch: {
    visibleSessions() {
      this.$nextTick(() => {
        this.computeStickyTop();
        this.rebuildDayObserver();
        this._requestScrollMeasure();
      });
    },

    isFetching(newVal) {
      if (!newVal) {
        this.$nextTick(() => {
          this.computeStickyTop();
          this.rebuildDayObserver();
          this._requestScrollMeasure();
        });
      }
    },

    currentView() {
      this.$nextTick(() => {
        this.computeStickyTop();
        this.rebuildDayObserver();
        this._requestScrollMeasure();
      });
    },

    tabs: {
      handler() {
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.computeStickyTop();
            this.rebuildDayObserver();
            this._requestScrollMeasure();
          });
        });
      },
      deep: true,
      immediate: true
    },

    windowWidth(newVal, oldVal) {
      const wasMobile = oldVal < 900;
      const isMobile = newVal < 900;

      if (wasMobile !== isMobile) {
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.computeStickyTop();
            this.rebuildDayObserver();
            this._requestScrollMeasure();
          });
        });
      }
    }
  },
  methods: {
    switchView(view) {
      this.currentView = view;
    },

    goToCertificatePage() {
      this.$router.push({name: "My Certificate"});
    },

    _cssVarPx(name, fallback = 0) {
      const raw = getComputedStyle(document.documentElement).getPropertyValue(name);
      const n = parseInt(raw, 10);
      return Number.isFinite(n) ? n : fallback;
    },

    computeStickyTop() {
      if (!this.isMobile) {
        this.computedStickyTop =
          this._cssVarPx('--attendee-header-height', 80) +
          this._cssVarPx('--attendee-gap-md', 0) +
          this._cssVarPx('--attendee-gap-lg', 0);
        return;
      }

      let top = this._cssVarPx('--attendee-header-mobile-height', 56);

      const tabsEl = document.querySelector('.scheduler-mobile-tabs');
      if (tabsEl && tabsEl.offsetParent !== null) {
        const tabsHeight = Math.round(tabsEl.getBoundingClientRect().height);
        if (tabsHeight > 0) {
          top += tabsHeight;
        }
      }

      this.computedStickyTop = top;
      document.documentElement.style.setProperty('--agenda-sticky-offset', `${top}px`);
    },

    getStickyTopOffset() {
      return this.computedStickyTop;
    },

    initDayObserver() {
      const topOffset = this.getStickyTopOffset();
      this._dayIO = new IntersectionObserver(
        this.onDayIntersect,
        {
          root: null,
          rootMargin: `-${topOffset}px 0px -85% 0px`,
          threshold: 0,
        }
      );
    },

    observeDayHeaders() {
      const container = this.$refs.agendaGroup;
      if (!container) return;

      const headers = container.querySelectorAll('.agenda-list-day');
      this._observedDayHeaders = Array.from(headers);

      if (!this._observedDayHeaders || !this._observedDayHeaders.length) {
        return;
      }

      this._observedDayHeaders.forEach((el) => {
        if (!el.dataset.dayLabel) {
          const h3 = el.querySelector('h3');
          el.dataset.dayLabel = h3 ? h3.textContent.trim() : '';
        }
        this._dayIO.observe(el);
      });
    },

    disconnectDayObserver() {
      if (this._dayIO) {
        if (this._observedDayHeaders && this._observedDayHeaders.length) {
          this._observedDayHeaders.forEach((el) => this._dayIO.unobserve(el));
        }
        this._dayIO.disconnect();
        this._dayIO = null;
      }
      this._observedDayHeaders = [];
    },

    onDayIntersect(entries) {
      let latestLabel = null;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          latestLabel = entry.target.dataset.dayLabel || '';
        }
      });

      if (latestLabel) {
        this.currentStickyDay = latestLabel;
        return;
      }

      const topOffset = this.getStickyTopOffset();
      const headers = this._observedDayHeaders;
      if (!headers.length) {
        this.currentStickyDay = '';
        return;
      }

      let candidate = '';
      for (const el of headers) {
        const rect = el.getBoundingClientRect();
        if (rect.top - topOffset <= 0) {
          candidate = el.dataset.dayLabel || '';
        } else {
          break;
        }
      }
      if (!candidate) {
        candidate = headers[0].dataset.dayLabel || '';
      }
      this.currentStickyDay = candidate;
    },

    syncStickyWithFirstDay() {
      if (this.visibleSessions.length) {
        this.currentStickyDay = this.formatDate(this.visibleSessions[0].start_at);
      } else {
        this.currentStickyDay = '';
      }
      this.stickyPushOffset = 0;
    },

    rebuildDayObserver() {
      this.disconnectDayObserver();

      this.$nextTick(() => {
        this.initDayObserver();
        this.observeDayHeaders();
        this.syncStickyWithFirstDay();
      });
    },

    formatDate(dateStr) {
      if (!dateStr) return '';

      const pureDate = dateStr.slice(0, 10);
      const [year, month, day] = pureDate.split('-').map(Number);
      const date = new Date(year, month - 1, day);

      if (isNaN(date.getTime())) {
        console.warn('Invalid date string:', dateStr);
        return pureDate;
      }

      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
      return date.toLocaleDateString('en-US', options);
    },

    shouldShowDay(index) {
      if (index === 0) return true;

      const current = this.visibleSessions[index];
      const previous = this.visibleSessions[index - 1];

      return current.start_at.slice(0, 10) !== previous.start_at.slice(0, 10);
    },

    handleVisibilityChange(isVisible) {
      if (isVisible) {
        this.loadMore();
      }
    },

    loadMore() {
      const visibleSessionsCount = Math.min(
        this.visibleSessionsCount + this.defaultSessionsCount,
        this.filteredSessions.length
      );
      this.$store.commit("agendaStore/updateVisibleSessionsCount", visibleSessionsCount);

      this.$nextTick(() => this._requestScrollMeasure());
    },

    _requestScrollMeasure() {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = 0;
        this.updateStickyDayBar();
      });
    },

    updateStickyDayBar() {
      const groupEl = this.$refs.agendaGroup;
      if (!groupEl) return;

      const headers = Array.from(groupEl.querySelectorAll('.agenda-list-day'));
      if (!headers.length) {
        this.currentStickyDay = '';
        this.stickyPushOffset = 0;
        return;
      }

      const topOffset = this.getStickyTopOffset();
      const stickyBarHeight = 60; // Approximate height of sticky bar

      let activeIdx = 0;
      for (let i = 0; i < headers.length; i++) {
        const rect = headers[i].getBoundingClientRect();
        if (rect.top <= topOffset + stickyBarHeight) {
          activeIdx = i;
        } else {
          break;
        }
      }

      const activeHeader = headers[activeIdx];
      const titleEl = activeHeader?.querySelector('h3');
      const title = titleEl ? titleEl.textContent.trim() : '';

      if (this.currentStickyDay !== title) {
        this.currentStickyDay = title;
      }

      const stickyBar = this.$refs.stickyDayBar;
      const barRect = stickyBar ? stickyBar.getBoundingClientRect() : { height: 0 };
      const nextHeader = headers[activeIdx + 1];

      if (nextHeader) {
        const nextRect = nextHeader.getBoundingClientRect();
        const delta = nextRect.top - topOffset - barRect.height;
        this.stickyPushOffset = delta < 0 ? Math.min(barRect.height, Math.abs(delta)) : 0;
      } else {
        this.stickyPushOffset = 0;
      }
    },

    handleAddToSchedule(session) {
      this.$store.dispatch('agendaStore/addToSchedule', {
        sessionId: session.id,
        attendeeId: this.attendeeId
      }).then(() => {
        this.$store.dispatch("agendaStore/fetchAgenda", {
          attendeeId: this.attendeeId,
          conferenceId: this.attendee?.conference_id
        });
      });
    },

    handleOpenSession(session) {
      this.$router.push({
        name: 'Session Details',
        params: {
          attendeeId: this.attendeeId,
          sessionId: session.id
        }
      });
    },
  },
}
</script>

<style scoped lang="scss">
.scheduler-card {
  padding: var(--attendee-gap-md);
  background-color: white;
  border-radius: var(--attendee-card-border-radius);
  box-shadow: var(--attendee-box-shadow);
  width: 100%;
  min-width: 1px;

  @media (max-width: 900px) {
    padding: 0;
    box-shadow: none;
    border-radius: 0;
  }
}

/* КЛЮЧОВЕ ВИПРАВЛЕННЯ: Приховуємо перший день повністю */
.agenda-list.first-item .agenda-list-day {
  visibility: hidden;
  height: 0 !important;
  min-height: 0 !important;
  margin: 0 !important;
  padding: 0 !important;
  border: none !important;
  overflow: hidden;
  line-height: 0;
}

/* Global sticky bar */
.sticky-day-header {
  position: sticky;
  top: var(--agenda-sticky-offset);
  z-index: 21;
  background-color: white;
  padding: var(--attendee-gap-md) 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: transform 0.1s ease-out;

  @media (max-width: 900px) {
    padding: 5px var(--attendee-gap-md) !important;
  }
}

.sticky-day-header__left {
  display: flex;
  align-items: center;
  gap: var(--attendee-gap-md);

  h3 {
    margin: 0 !important;
    color: var(--attendee-text-primary-color);
    font-size: 21px;

    @media (max-width: 900px) {
      font-size: 16px !important;
      text-align: left !important;
      padding: 0 !important;
    }
  }
}

/* Per-day in-flow headers */
.agenda-list-day {
  position: sticky;
  top: var(--agenda-sticky-offset);
  background-color: white;
  padding: var(--attendee-gap-md) 0;
  z-index: 18;

  h3 {
    margin-bottom: 0;
    color: var(--attendee-text-primary-color);
    font-size: 21px;

    @media (max-width: 900px) {
      font-size: 16px;
    }
  }

  @media (max-width: 900px) {
    padding: 0 var(--attendee-gap-md);
  }
}

.load-more-trigger {
  height: 1px;
  text-align: center;
}

.agenda-page {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: var(--attendee-gap-lg);
  max-width: 100%;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    margin-top: 0;
  }

  h3 {
    color: var(--attendee-text-primary-color);
    font-size: 21px;
    margin-bottom: var(--attendee-gap-md);

    @media (max-width: 900px) {
      font-size: 16px;
      padding: var(--attendee-gap-md);
      padding-left: 0;
      margin-bottom: 0;
      text-align: left;
    }
  }
}

.agenda-day {
  margin-bottom: 50px;

  @media (max-width: 900px) {
    margin-bottom: 20px;
  }
}

@media (min-width: 901px) {
  .mtb-desktop-15 {
    margin-top: 15px !important;
    margin-bottom: 15px !important;
  }
}
</style>
